import { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertTriangle, HelpCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolveRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({
        title: opts.title || "Are you sure?",
        description: opts.description || "",
        confirmLabel: opts.confirmLabel || "Confirm",
        cancelLabel: opts.cancelLabel || "Cancel",
        danger: !!opts.danger,
        testId: opts.testId || "confirm-dialog",
      });
    });
  }, []);

  const close = (result) => {
    const r = resolveRef.current;
    resolveRef.current = null;
    setState(null);
    if (r) r(result);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!state} onOpenChange={(open) => { if (!open) close(false); }}>
        {state && (
          <AlertDialogContent
            data-testid={state.testId}
            className="rounded-xl border border-[#E5E7EB] bg-white p-0 max-w-md gap-0"
          >
            <AlertDialogHeader className="px-6 pt-6 pb-3 space-y-3">
              <div
                className={`flex items-center justify-center w-11 h-11 rounded-lg ${
                  state.danger
                    ? "bg-[#EF4444]/10 text-[#B91C1C]"
                    : "bg-[#3B82F6]/10 text-[#3B82F6]"
                }`}
              >
                {state.danger ? <AlertTriangle size={18} /> : <HelpCircle size={18} />}
              </div>
              <AlertDialogTitle className="font-['Geist'] font-semibold tracking-tight text-2xl leading-tight text-[#0F172A]">
                {state.title}
              </AlertDialogTitle>
              {state.description && (
                <AlertDialogDescription className="text-sm text-[#475569] leading-relaxed pt-1">
                  {state.description}
                </AlertDialogDescription>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter className="px-6 pb-6 pt-2 flex-row justify-end gap-2 sm:gap-2">
              <AlertDialogCancel
                onClick={() => close(false)}
                data-testid={`${state.testId}-cancel`}
                className="rounded-md border border-[#E5E7EB] bg-white text-[#0F172A] text-sm font-medium h-9 px-4 mt-0 hover:bg-[#F8FAFC]"
              >
                {state.cancelLabel}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={() => close(true)}
                data-testid={`${state.testId}-confirm`}
                className={`rounded-md text-white text-sm font-medium h-9 px-4 ${
                  state.danger
                    ? "bg-[#EF4444] hover:bg-[#DC2626]"
                    : "bg-[#3B82F6] hover:bg-[#2563EB]"
                }`}
              >
                {state.confirmLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used inside a <ConfirmProvider>");
  }
  return ctx;
};
